import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  Plus, ChevronLeft, ChevronRight, CalendarDays, Sun, Heart, Briefcase,
  Award, Coffee, Hourglass, Download, FileText, CheckCircle2, XCircle, Users,
  AlertCircle,
} from "lucide-react";
import { toast } from "sonner";
import {
  PageHeader, KPICard, KPIGrid, FilterBar, StatusBadge, SectionCard, PillTabs, EmptyState,
} from "@/components/app/PageKit";

const TYPE_CFG = {
  casual:   { dot: "bg-cyan-500",    bg: "bg-cyan-50",    text: "text-cyan-700",    ring: "ring-cyan-100",    icon: Coffee,     label: "Casual" },
  sick:     { dot: "bg-rose-500",    bg: "bg-rose-50",    text: "text-rose-700",    ring: "ring-rose-100",    icon: Heart,      label: "Sick" },
  earned:   { dot: "bg-emerald-500", bg: "bg-emerald-50", text: "text-emerald-700", ring: "ring-emerald-100", icon: Award,      label: "Earned" },
  comp_off: { dot: "bg-violet-500",  bg: "bg-violet-50",  text: "text-violet-700",  ring: "ring-violet-100",  icon: Briefcase,  label: "Comp Off" },
  annual:   { dot: "bg-blue-500",    bg: "bg-blue-50",    text: "text-blue-700",    ring: "ring-blue-100",    icon: Sun,        label: "Annual" },
  weekend:  { dot: "bg-slate-300",   bg: "bg-slate-50",   text: "text-slate-500",   ring: "ring-slate-100",   icon: CalendarDays, label: "Weekend" },
  holiday:  { dot: "bg-cyan-400",    bg: "bg-cyan-50",    text: "text-cyan-700",    ring: "ring-cyan-100",    icon: Sun,        label: "Holiday" },
};

const initials = (n = "U") => n.split(" ").filter(Boolean).slice(0, 2).map((s) => s[0]?.toUpperCase()).join("");

/* ============== Leave row in approvals/list ============== */
function LeaveRow({ l, onApprove, onReject, employees }) {
  const cfg = TYPE_CFG[l.leave_type] || TYPE_CFG.casual;
  const emp = employees.find((e) => e.id === l.employee_id) || l.employee;
  return (
    <div className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 transition" data-testid={`leave-row-${l.id}`}>
      <Avatar className="w-9 h-9 ring-2 ring-white shadow-sm shrink-0">
        {emp?.avatar_url && <AvatarImage src={emp.avatar_url} alt={emp.full_name} />}
        <AvatarFallback className="bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white text-[10px] font-bold">{initials(emp?.full_name)}</AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[13px] font-semibold text-slate-900 truncate">{emp?.full_name || l.employee_id}</span>
          <Badge className={`${cfg.bg} ${cfg.text} border-0 text-[10px] font-bold`}>{cfg.label}</Badge>
        </div>
        <div className="text-[11px] text-slate-500 truncate">
          {new Date(l.start_date).toLocaleDateString([], { month: "short", day: "numeric" })} – {new Date(l.end_date).toLocaleDateString([], { month: "short", day: "numeric" })} · {l.days}d
          {l.reason && ` · "${l.reason}"`}
        </div>
      </div>
      {onApprove && l.status === "pending" ? (
        <div className="flex items-center gap-1.5 shrink-0">
          <Button size="sm" onClick={() => onApprove(l.id)} className="h-8 px-3 bg-emerald-600 hover:bg-emerald-700 text-white text-[11px]" data-testid={`approve-${l.id}`}>Approve</Button>
          <Button size="sm" variant="outline" onClick={() => onReject(l.id)} className="h-8 px-3 border-rose-200 text-rose-700 hover:bg-rose-50 text-[11px]" data-testid={`reject-${l.id}`}>Reject</Button>
        </div>
      ) : (
        <StatusBadge status={l.status} />
      )}
    </div>
  );
}

export default function Leaves() {
  const [mine, setMine] = useState([]);
  const [pending, setPending] = useState([]);
  const [all, setAll] = useState([]);
  const [balance, setBalance] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [tab, setTab] = useState("mine");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ leave_type: "casual", start_date: "", end_date: "", reason: "" });
  const now = new Date();
  const [ym, setYm] = useState({ y: now.getFullYear(), m: now.getMonth() + 1 });

  const load = async () => {
    try {
      const [m, p, a, b, e] = await Promise.all([
        api.get("/leaves", { params: { scope: "mine" } }),
        api.get("/leaves", { params: { scope: "pending-me" } }),
        api.get("/leaves"),
        api.get("/leaves/balance"),
        api.get("/employees").catch(() => ({ data: [] })),
      ]);
      setMine(m.data || []); setPending(p.data || []); setAll(a.data || []); setBalance(b.data || []); setEmployees(e.data || []);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const lloc = useLocation();
  useEffect(() => { if (lloc.state?.openAdd) setOpen(true); }, [lloc.state]);

  const submit = async () => {
    try {
      await api.post("/leaves", form);
      toast.success("Leave applied");
      setOpen(false); setForm({ leave_type: "casual", start_date: "", end_date: "", reason: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const act = async (id, action) => {
    try { await api.post(`/leaves/${id}/action`, { action }); toast.success(`Leave ${action}d`); load(); }
    catch { toast.error("Failed"); }
  };

  const todayStr = new Date().toISOString().slice(0, 10);
  // Who's off today
  const onLeaveToday = useMemo(() => all.filter(l =>
    l.status === "approved" && l.start_date <= todayStr && l.end_date >= todayStr
  ), [all, todayStr]);

  const upcomingLeaves = useMemo(() =>
    all.filter(l => l.status === "approved" && l.start_date > todayStr).sort((a, b) => a.start_date.localeCompare(b.start_date)).slice(0, 5)
  , [all, todayStr]);

  // Build month grid
  const monthName = new Date(ym.y, ym.m - 1).toLocaleString("en", { month: "long" });
  const daysInMonth = new Date(ym.y, ym.m, 0).getDate();
  const firstDay = new Date(ym.y, ym.m - 1, 1).getDay();

  const cellMap = useMemo(() => {
    const map = {};
    for (let d = 1; d <= daysInMonth; d++) {
      const iso = `${ym.y}-${String(ym.m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      const dow = new Date(ym.y, ym.m - 1, d).getDay();
      map[iso] = { date: iso, day: d, type: (dow === 0 || dow === 6) ? "weekend" : null, leaves: [] };
    }
    all.forEach(l => {
      if (l.status === "rejected") return;
      const start = new Date(l.start_date), end = new Date(l.end_date);
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const iso = d.toISOString().slice(0, 10);
        if (map[iso]) {
          map[iso].leaves.push(l);
          if (!map[iso].type || map[iso].type === "weekend") map[iso].type = l.leave_type;
        }
      }
    });
    return map;
  }, [all, ym, daysInMonth]);

  const prevMonth = () => setYm(({ y, m }) => m === 1 ? { y: y - 1, m: 12 } : { y, m: m - 1 });
  const nextMonth = () => setYm(({ y, m }) => m === 12 ? { y: y + 1, m: 1 } : { y, m: m + 1 });

  // KPI: balances
  const bal = (type) => balance.find(b => b.type === type);

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5 min-w-0" data-testid="leaves-page">
      <PageHeader
        category="TIME OFF"
        title="Leave & Absence"
        description="Manage leave balances, requests, approvals, and team availability — all in one place."
        primaryAction={{ label: "Apply Leave", icon: Plus, onClick: () => setOpen(true), testid: "apply-leave-btn" }}
        secondaryAction={{ label: "Export", icon: Download, onClick: () => toast.info("Export starting") }}
        moreActions={[
          { label: "Leave Policies", icon: FileText, onClick: () => toast.info("Open policies") },
        ]}
      />

      {/* KPI cards with progress bars */}
      <KPIGrid cols={5}>
        {[
          { type: "casual",   label: "Casual Leave",   tone: "cyan"    },
          { type: "sick",     label: "Sick Leave",     tone: "rose"    },
          { type: "earned",   label: "Earned Leave",   tone: "emerald" },
          { type: "comp_off", label: "Comp Off",       tone: "violet"  },
        ].map(({ type, label, tone }) => {
          const b = bal(type) || { remaining: 0, total: 0, used: 0 };
          const pct = b.total ? Math.min(100, (b.remaining / b.total) * 100) : 0;
          const cfg = TYPE_CFG[type];
          return (
            <KPICard
              key={type}
              icon={cfg.icon}
              tone={tone}
              label={label}
              value={`${b.remaining}/${b.total}`}
              sub={`${b.used} used · ${b.remaining} remaining`}
              testid={`balance-${type}`}
              footer={
                <div className="mt-3 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                  <div className={cfg.dot.replace("bg-", "bg-") + " h-full"} style={{ width: `${pct}%` }} />
                </div>
              }
            />
          );
        })}
        <KPICard icon={Hourglass} tone="amber" label="Pending Requests" value={pending.length} sub={pending.length ? "Need your action" : "All caught up"} testid="balance-pending" />
      </KPIGrid>

      {/* Tabs */}
      <PillTabs
        value={tab} onChange={setTab}
        tabs={[
          { value: "mine",     label: "My Leave",          badge: mine.length },
          { value: "calendar", label: "Team Calendar"                          },
          { value: "pending",  label: "Pending Approvals", badge: pending.length },
          { value: "policies", label: "Leave Policies"                         },
        ]}
        testid="leaves-tabs"
      />

      {/* Main content + right rail */}
      <div className="grid xl:grid-cols-[1fr_320px] gap-5 min-w-0">
        <div className="min-w-0 space-y-5">
          {/* MY LEAVE */}
          {tab === "mine" && (
            <SectionCard icon={CalendarDays} title="My Leave Requests" sub={`${mine.length} total`}
              action={<Button size="sm" onClick={() => setOpen(true)} className="bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-[12px]"><Plus className="w-3.5 h-3.5 mr-1" /> Apply</Button>}
            >
              {mine.length === 0 ? (
                <EmptyState icon={CalendarDays} title="No leave requests yet" message="Apply for your first leave — vacation, sick day, or comp-off." action={{ label: "Apply Leave", icon: Plus, onClick: () => setOpen(true) }} />
              ) : (
                <div className="space-y-1">
                  {mine.map((l) => <LeaveRow key={l.id} l={l} employees={employees} />)}
                </div>
              )}
            </SectionCard>
          )}

          {/* TEAM CALENDAR */}
          {tab === "calendar" && (
            <Card className="p-5 border-slate-200 shadow-sm bg-white" data-testid="leaves-calendar">
              <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
                <div className="flex items-center gap-2">
                  <h2 className="text-2xl font-bold font-[Outfit] tracking-tight text-slate-900">{monthName} {ym.y}</h2>
                  <button onClick={prevMonth} className="w-8 h-8 rounded-lg border border-slate-200 hover:bg-slate-50 flex items-center justify-center text-slate-500"><ChevronLeft className="w-4 h-4" /></button>
                  <button onClick={nextMonth} className="w-8 h-8 rounded-lg border border-slate-200 hover:bg-slate-50 flex items-center justify-center text-slate-500"><ChevronRight className="w-4 h-4" /></button>
                </div>
                <div className="text-[11px] text-slate-500">Click any day to see who's off</div>
              </div>

              <div className="grid grid-cols-7 gap-1.5 mb-2">
                {["SUN","MON","TUE","WED","THU","FRI","SAT"].map((d) => (
                  <div key={d} className="text-[10px] uppercase tracking-[0.16em] font-bold text-slate-400 text-center pb-1">{d}</div>
                ))}
              </div>

              <div className="grid grid-cols-7 gap-1.5">
                {Array(firstDay).fill(0).map((_, i) => <div key={`pad-${i}`} className="aspect-[1.1/1]" />)}
                {Object.values(cellMap).map((c) => {
                  const cfg = TYPE_CFG[c.type] || { dot: "bg-slate-200", bg: "bg-white", text: "text-slate-700", ring: "ring-slate-100" };
                  const isToday = c.date === todayStr;
                  const count = c.leaves.length;
                  return (
                    <div key={c.date} className={`relative aspect-[1.1/1] rounded-lg ${cfg.bg} ring-1 ${isToday ? "ring-2 ring-blue-500" : cfg.ring} hover:shadow-sm transition flex flex-col items-center justify-start p-1.5`} data-testid={`leave-day-${c.date}`}>
                      <span className={`text-[13px] font-bold font-[Outfit] ${cfg.text}`}>{c.day}</span>
                      {count > 0 && (
                        <div className="absolute bottom-1 left-1.5 right-1.5 flex items-center gap-0.5 flex-wrap">
                          {c.leaves.slice(0, 3).map((l, i) => (
                            <span key={i} className={`w-1.5 h-1.5 rounded-full ${TYPE_CFG[l.leave_type]?.dot || "bg-slate-300"}`} />
                          ))}
                          {count > 3 && <span className="text-[8px] font-bold text-slate-500">+{count - 3}</span>}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="mt-4 flex flex-wrap items-center gap-3 pt-3 border-t border-slate-100">
                {Object.entries(TYPE_CFG).map(([k, c]) => (
                  <div key={k} className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-slate-600">
                    <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
                    {c.label}
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* PENDING APPROVALS */}
          {tab === "pending" && (
            <SectionCard icon={Hourglass} title="Pending Approvals" sub={`${pending.length} awaiting your decision`}>
              {pending.length === 0 ? (
                <EmptyState icon={CheckCircle2} title="All caught up!" message="No leave requests waiting for your approval." />
              ) : (
                <div className="space-y-1">
                  {pending.map((l) => <LeaveRow key={l.id} l={l} employees={employees} onApprove={(id) => act(id, "approve")} onReject={(id) => act(id, "reject")} />)}
                </div>
              )}
            </SectionCard>
          )}

          {/* POLICIES */}
          {tab === "policies" && (
            <div className="grid md:grid-cols-2 gap-4">
              {Object.entries(TYPE_CFG).filter(([k]) => !["weekend", "holiday", "annual"].includes(k)).map(([k, cfg]) => (
                <SectionCard key={k} icon={cfg.icon} title={`${cfg.label} Leave Policy`} sub="Annual entitlement & usage rules">
                  <ul className="text-[12px] text-slate-600 space-y-1.5 list-disc pl-4">
                    <li>{k === "earned" ? "21 days/year credited monthly" : k === "casual" ? "12 days/year (lapsed)" : k === "sick" ? "10 days/year (medical certificate for >2)" : "Earned via overtime/extra-day work"}</li>
                    <li>Carry-forward: {k === "earned" ? "up to 30 days" : "not applicable"}</li>
                    <li>Encashment: {k === "earned" ? "at exit, pro-rata" : "no"}</li>
                    <li>Approval flow: Manager → HR Admin</li>
                  </ul>
                </SectionCard>
              ))}
            </div>
          )}
        </div>

        {/* RIGHT RAIL */}
        <aside className="space-y-4 xl:sticky xl:top-20 xl:self-start">
          <SectionCard icon={Users} title="Who's Off Today" sub={`${onLeaveToday.length} employees on leave`}>
            {onLeaveToday.length === 0 ? (
              <div className="text-[12px] text-slate-400 text-center py-4">Everyone's in 🎉</div>
            ) : (
              <div className="space-y-3">
                {onLeaveToday.slice(0, 5).map((l) => {
                  const emp = employees.find(e => e.id === l.employee_id) || l.employee;
                  const cfg = TYPE_CFG[l.leave_type] || TYPE_CFG.casual;
                  return (
                    <div key={l.id} className="flex items-center gap-2.5">
                      <Avatar className="w-9 h-9 ring-2 ring-white">
                        {emp?.avatar_url && <AvatarImage src={emp.avatar_url} alt={emp.full_name} />}
                        <AvatarFallback className="bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white text-[10px] font-bold">{initials(emp?.full_name)}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="text-[13px] font-semibold text-slate-900 truncate">{emp?.full_name}</div>
                        <div className="text-[11px] text-slate-500 truncate">{emp?.designation || ""}</div>
                      </div>
                      <Badge className={`${cfg.bg} ${cfg.text} border-0 text-[10px] font-bold`}>{cfg.label}</Badge>
                    </div>
                  );
                })}
              </div>
            )}
          </SectionCard>

          <SectionCard icon={CalendarDays} title="Upcoming Leaves" sub="Next 5 approved">
            {upcomingLeaves.length === 0 ? (
              <div className="text-[12px] text-slate-400 text-center py-4">No upcoming leaves</div>
            ) : (
              <div className="space-y-3">
                {upcomingLeaves.map((l) => {
                  const emp = employees.find(e => e.id === l.employee_id) || l.employee;
                  const cfg = TYPE_CFG[l.leave_type] || TYPE_CFG.casual;
                  return (
                    <div key={l.id} className="flex items-center gap-2.5">
                      <span className={`w-1 h-9 rounded-full ${cfg.dot}`} />
                      <div className="flex-1 min-w-0">
                        <div className="text-[13px] font-semibold text-slate-900 truncate">{emp?.full_name}</div>
                        <div className="text-[11px] text-slate-500 truncate">{cfg.label} · {new Date(l.start_date).toLocaleDateString([], { month: "short", day: "numeric" })} – {new Date(l.end_date).toLocaleDateString([], { month: "short", day: "numeric" })}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </SectionCard>

          <SectionCard icon={AlertCircle} title="Conflicts & Alerts" sub="Watch list">
            <div className="space-y-2">
              {[
                { label: "Same-team overlap (May 12-13)", count: 3, tone: "amber" },
                { label: "Manager off + reportee request", count: 1, tone: "rose" },
              ].map((it) => {
                const t = { amber: "bg-amber-50 text-amber-700 ring-amber-100", rose: "bg-rose-50 text-rose-700 ring-rose-100" }[it.tone];
                return (
                  <div key={it.label} className={`p-2.5 rounded-lg ring-1 ${t}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[12px] font-semibold flex-1 min-w-0 truncate">{it.label}</span>
                      <span className="text-[11px] font-bold tabular-nums">{it.count}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </SectionCard>
        </aside>
      </div>

      {/* Apply Leave Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-[Outfit]">Apply for Leave</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Leave Type</Label>
              <Select value={form.leave_type} onValueChange={(v) => setForm({ ...form, leave_type: v })}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="casual">Casual</SelectItem>
                  <SelectItem value="sick">Sick</SelectItem>
                  <SelectItem value="earned">Earned</SelectItem>
                  <SelectItem value="comp_off">Comp Off</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Start Date</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="mt-1" data-testid="leave-start" /></div>
              <div><Label>End Date</Label><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="mt-1" data-testid="leave-end" /></div>
            </div>
            <div><Label>Reason</Label><Textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="mt-1" rows={3} data-testid="leave-reason" /></div>
          </div>
          <DialogFooter><Button onClick={submit} className="qh-grad-bg text-white" data-testid="submit-leave">Submit Request</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
